import { useState } from "react";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import { FiEdit3, FiSave, FiX, FiCheck } from "react-icons/fi";
import { useMutation, useQueryClient } from "react-query";
import { IOrg } from "../../models/org";
import { updateOrg } from "../../api/org";
import { useOrgContext } from "../../context/org";

type Props = {
    org: IOrg;
};

function BasicSettings({ org }: Props) {
    const [orgName, setOrgName] = useState(org.name);
    const [description, setDescription] = useState(org.description);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [edit, setEdit] = useState(false);

    const { membership } = useOrgContext();
    const allowed = ["owner", "admin", "editor"].includes(
        membership?.role || "",
    );
    const disabled = !edit || !allowed;

    const queryClient = useQueryClient();
    const { mutateAsync: updateOrgFn, isLoading } = useMutation(updateOrg, {
        onSuccess: () => {
            queryClient.invalidateQueries(["Organizations"]);
            setMessage("Organization updated successfully");
            setError(null);
            setEdit(false);
        },
        onError: (error) => {
            setError("Unexpected error. Please try again or contact admin.");
            setMessage(null);
        },
    });

    const handleSubmit = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        if (!orgName.trim()) {
            setError("Organization name is required");
            return;
        }

        updateOrgFn({
            id: org.id,
            name: orgName.trim(),
            description: description.trim(),
        });
    };

    const handleCancel = () => {
        setEdit(false);
        setOrgName(org.name);
        setDescription(org.description);
        setError(null);
        setMessage(null);
    };

    return (
        <Card className="bg-dark border-secondary">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h5 className="text-white mb-1">
                            Organization Details
                        </h5>
                        <p className="text-secondary mb-0 small">
                            Update your organization's basic information
                        </p>
                    </div>
                    {!edit && allowed && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setEdit(true)}
                            className="d-flex align-items-center gap-2"
                        >
                            <FiEdit3 size={14} />
                            Edit
                        </Button>
                    )}
                </div>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">
                            Organization Name *
                        </Form.Label>
                        <Form.Control
                            disabled={disabled}
                            type="text"
                            value={orgName}
                            placeholder="Enter organization name"
                            onChange={(e) => setOrgName(e.target.value)}
                            className={`text-white border-secondary ${disabled ? 'bg-secondary bg-opacity-25' : 'bg-dark'}`}
                            style={
                                {
                                    borderColor:
                                        error && !orgName.trim()
                                            ? "#dc3545"
                                            : "#6c757d",
                                    "--bs-border-color": "#6c757d",
                                    "--bs-focus-border-color": "#0d6efd",
                                    backgroundColor: disabled ? 'rgba(108, 117, 125, 0.25)' : '#212529',
                                    color: disabled ? '#adb5bd' : '#ffffff'
                                } as any
                            }
                        />
                        <Form.Text className="text-light">
                            This name will be visible to all organization
                            members
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="text-white fw-semibold">
                            Description
                        </Form.Label>
                        <Form.Control
                            disabled={disabled}
                            as="textarea"
                            rows={3}
                            value={description}
                            placeholder="Describe your organization's purpose or focus areas..."
                            onChange={(e) => setDescription(e.target.value)}
                            className={`text-white border-secondary ${disabled ? 'bg-secondary bg-opacity-25' : 'bg-dark'}`}
                            style={
                                {
                                    borderColor: "#6c757d",
                                    "--bs-border-color": "#6c757d",
                                    "--bs-focus-border-color": "#0d6efd",
                                    resize: "none",
                                    backgroundColor: disabled ? 'rgba(108, 117, 125, 0.25)' : '#212529',
                                    color: disabled ? '#adb5bd' : '#ffffff'
                                } as any
                            }
                        />
                        <Form.Text className="text-light">
                            Help team members understand what this organization
                            is for
                        </Form.Text>
                    </Form.Group>

                    {error && (
                        <Alert
                            variant="danger"
                            className="d-flex align-items-center mb-3"
                            style={{
                                backgroundColor: "rgba(220, 53, 69, 0.2)",
                                borderColor: "#dc3545",
                                color: "#ffffff",
                            }}
                        >
                            <FiX className="me-2" size={16} />
                            {error}
                        </Alert>
                    )}

                    {message && (
                        <Alert
                            variant="success"
                            className="d-flex align-items-center mb-3"
                            style={{
                                backgroundColor: "rgba(25, 135, 84, 0.2)",
                                borderColor: "#198754",
                                color: "#ffffff",
                            }}
                        >
                            <FiCheck className="me-2" size={16} />
                            {message}
                        </Alert>
                    )}

                    {!allowed && (
                        <Alert
                            variant="warning"
                            className="mb-3"
                            style={{
                                backgroundColor: "rgba(255, 193, 7, 0.2)",
                                borderColor: "#ffc107",
                                color: "#ffffff",
                            }}
                        >
                            You don't have permission to edit organization
                            settings
                        </Alert>
                    )}

                    {edit && (
                        <div className="d-flex gap-2 justify-content-end">
                            <Button
                                type="submit"
                                disabled={isLoading || !orgName.trim()}
                                variant="primary"
                                className="d-flex align-items-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <FiSave size={14} />
                                        Save Changes
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={handleCancel}
                                disabled={isLoading}
                                className="d-flex align-items-center gap-2"
                            >
                                <FiX size={14} />
                                Cancel
                            </Button>
                        </div>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );
}

export default BasicSettings;
